import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import { PipelineStats, PipelineSummaryStats, PipelineStatus } from '../../../const/PipelineTypes';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';

const DEFAULT_STATS: PipelineStats = {
  [PipelineStatus.InProgress]: 0,
  [PipelineStatus.Success]: 0,
  [PipelineStatus.Failure]: 0,
  [PipelineStatus.Canceled]: 0,
  [PipelineStatus.Pending]: 0,
  [PipelineStatus.Completed]: 0,
  [PipelineStatus.Banned]: 0,
  [PipelineStatus.Manual]: 0,
  [PipelineStatus.Warning]: 0,
  [PipelineStatus.Aborted]: 0,
  [PipelineStatus.Paused]: 0
};

export async function getWorkflowStatusByDateRange(startDate: string, endDate: string): Promise<PipelineStats> {
  const startISO = toStartOfDayISO(startDate);
  const endISO = toEndOfDayISO(endDate);
  const url = `/api/workflows/status-by-date-range?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
  const { data } = await api.get(url);
  return data ?? {};
}

export function useDashboardSummaryByDateRange(startDate: string, endDate: string): PipelineSummaryStats {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = endDate === new Date().toISOString().slice(0, 10);

  const query = useQuery<PipelineStats>({
    queryKey: ['workflow-status-by-date-range', startDate, endDate],
    queryFn: () => getWorkflowStatusByDateRange(startDate, endDate),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    refetchInterval: !hasError && endDateIsToday ? 60000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Dashboard Summary',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching workflow status by date range.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  const errorMessage = query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null;

  return {
    stats: query.data ?? DEFAULT_STATS,
    isLoading: query.isLoading,
    error: query.isError ? errorMessage : null
  };
}
